// src/components/CV.js
import React, { useState, useEffect } from 'react';
import './CV.css';

const CV = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const calculateDuration = (startDate, endDate = null) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();

    const years = end.getFullYear() - start.getFullYear();
    const months = end.getMonth() - start.getMonth();

    const totalMonths = years * 12 + months;
    const displayYears = Math.floor(totalMonths / 12);
    const displayMonths = totalMonths % 12;

    return `${displayYears} yr${displayYears !== 1 ? 's' : ''} ${displayMonths} mo${displayMonths !== 1 ? 's' : ''}`;
  };

  const skills = [
    { category: 'Programming', items: ['Python', 'JavaScript/TypeScript', 'Groovy', 'PHP'] },
    { category: 'Cloud & DevOps', items: ['AWS', 'Docker', 'Kubernetes', 'Jenkins', 'GitLab'] },
    { category: 'Frontend', items: ['React', 'Redux', 'HTML5', 'CSS3', 'Tailwind'] },
    { category: 'Databases', items: ['DynamoDB', 'RDS', 'PostgreSQL', 'MongoDB'] }
  ];

  const experiences = [
    {
      id: 1,
      company: 'KONE',
      position: 'Senior DevOps Engineer',
      period: 'Mar 2022 - Present',
      duration: calculateDuration('2022-03'),
      location: 'Espoo, Finland',
      type: 'Full-time',
      description: 'Responsible for developing and maintaining solutions used to develop and run Kone solutions.',
      highlights: [
        'Cloud architecture and engineering: AWS',
        'Services: S3, EC2, Lambda, CloudFront, EKS, ECR, Cognito, Amplify, Athena, RDS, DynamoDb',
        'Version control, pipelines: Gitlab, Jenkins',
        'Languages: Python, Typescript/Javascript (React, Redux)'
      ]
    },
    {
      id: 2,
      company: 'Nordea',
      position: 'Senior Software Engineer',
      period: 'Mar 2018 - Mar 2022',
      duration: '4 yrs 1 mo',
      location: 'Helsinki, Finland',
      type: 'Full-time',
      description: 'Responsible for developing and maintaining Development and Production solutions hosting Nordea\'s microservices.',
      highlights: [
        'Microservices architecture',
        'Production environment management',
        'Development solutions optimization'
      ]
    },
    {
      id: 3,
      company: 'Qvantel',
      position: 'Release Manager',
      period: 'Jan 2017 - Mar 2018',
      duration: '1 yr 3 mos',
      location: 'Helsinki, Finland',
      type: 'Full-time',
      description: 'Cloud engineering and release management responsibilities.',
      highlights: [
        'Amazon Cloud administration',
        'Google Cloud Platform',
        'VMware virtualization'
      ]
    },
    {
      id: 4,
      company: 'Microsoft',
      position: 'Senior Specialist',
      period: 'May 2014 - Jan 2017',
      duration: '2 yrs 9 mos',
      location: 'Salo, Finland',
      type: 'Full-time',
      description: 'Responsible for Scheduler Build System and Infrastructure.',
      highlights: [
        'Build infrastructure operational management',
        'Tools: Perforce, Jenkins, MSBuild',
        'Scripting: Java, Groovy, Python, PowerShell'
      ]
    },
    {
      id: 5,
      company: 'Nokia',
      position: 'Build System Manager',
      period: 'Jul 2011 - May 2014',
      duration: '2 yrs 11 mos',
      location: 'Tampere, Finland',
      type: 'Full-time',
      description: 'Build System Support, Maintenance, and Optimization for Windows Phone.',
      highlights: [
        'Build system optimization',
        'Development team support',
        'Windows Phone platform'
      ]
    },
    {
      id: 6,
      company: 'Nokia',
      position: 'SW Asset Manager',
      period: 'Jan 2010 - Jul 2011',
      duration: '1 yr 7 mos',
      location: 'Tampere, Finland',
      type: 'Full-time',
      description: 'Product Platforms Product Management team responsibilities.',
      highlights: [
        'Configurations Content Management and Definition',
        'Business Justifications and Requirements Quality',
        'SW/HW configurations for S60 products'
      ]
    },
    {
      id: 7,
      company: 'Nokia',
      position: 'S60 WLAN Core SW Project Manager',
      period: '2004 - 2006',
      duration: '2 yrs',
      location: 'Tampere, Finland',
      type: 'Full-time',
      description: 'Leading R&D team responsible for WLAN solution development.',
      highlights: [
        'WLAN solution for Symbian and ISA mobile phones',
        'Project Planning and Scheduling',
        'WLAN, WiMax, CCX certification, EAP'
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  return (
    <div className="cv-modern-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-content">
          <div className="profile-image-container">
            <img src="CV.jpg" alt="Kirill Dremov" className="profile-image" />
            <div className="profile-ring"></div>
          </div>
          <div className="hero-text">
            <h1 className="hero-name">Kirill Dremov</h1>
            <p className="hero-title">Senior DevOps Engineer</p>
            <p className="hero-subtitle">Experienced Software Engineer specializing in Cloud Architecture & DevOps</p>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">20+</span>
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="stat">
                <span className="stat-number">5+</span>
                <span className="stat-label">Major Companies</span>
              </div>
              <div className="stat">
                <span className="stat-number">AWS</span>
                <span className="stat-label">Cloud Expert</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="contact-cards">
          <div className="contact-card">
            <div className="contact-icon">📧</div>
            <div className="contact-info">
              <span className="contact-label">Email</span>
              <a href="mailto:kirdrem@gmail.com" className="contact-value">kirdrem@gmail.com</a>
            </div>
          </div>
          <div className="contact-card">
            <div className="contact-icon">📱</div>
            <div className="contact-info">
              <span className="contact-label">Phone</span>
              <span className="contact-value">+358503853141</span>
            </div>
          </div>
          <div className="contact-card">
            <div className="contact-icon">💼</div>
            <div className="contact-info">
              <span className="contact-label">LinkedIn</span>
              <a href="https://www.linkedin.com/in/kirill-dremov" target="_blank" rel="noopener noreferrer" className="contact-value">
                linkedin.com/in/kirill-dremov
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="skills-section">
        <h2 className="section-title">Technical Skills</h2>
        <div className="skills-grid">
          {skills.map((skillGroup, index) => (
            <div key={index} className="skill-category">
              <h3 className="skill-category-title">{skillGroup.category}</h3>
              <div className="skill-tags">
                {skillGroup.items.map((skill, skillIndex) => (
                  <span key={skillIndex} className="skill-tag">{skill}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="experience-section">
        <h2 className="section-title">Professional Experience</h2>
        <div className="timeline">
          {experiences.map((exp, index) => (
            <div key={exp.id} className="timeline-item">
              <div className="timeline-marker">
                <div className="timeline-dot"></div>
                {index < experiences.length - 1 && <div className="timeline-line"></div>}
              </div>
              <div className="timeline-content">
                <div className="experience-card">
                  <div className="experience-header">
                    <div className="experience-main">
                      <h3 className="experience-position">{exp.position}</h3>
                      <h4 className="experience-company">{exp.company}</h4>
                    </div>
                    <div className="experience-meta">
                      <span className="experience-period">{exp.period}</span>
                      <span className="experience-duration">{exp.duration}</span>
                      <span className="experience-location">{exp.location}</span>
                    </div>
                  </div>
                  <p className="experience-description">{exp.description}</p>
                  <div className="experience-toggle">
                    <button 
                      className={`toggle-btn ${activeSection === exp.id ? 'active' : ''}`}
                      onClick={() => toggleSection(exp.id)}
                    >
                      {activeSection === exp.id ? 'Show Less' : 'Show Details'}
                      <span className="toggle-icon">{activeSection === exp.id ? '−' : '+'}</span>
                    </button>
                  </div>
                  {activeSection === exp.id && (
                    <div className="experience-details">
                      <ul className="experience-highlights">
                        {exp.highlights.map((highlight, hIndex) => (
                          <li key={hIndex}>{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Let's Work Together</h2>
          <p className="cta-subtitle">Ready to bring your next project to life with expert DevOps and cloud solutions?</p>
          <div className="cta-buttons">
            <a href="mailto:kirdrem@gmail.com" className="cta-btn primary">
              Get In Touch
            </a>
            <a href="https://www.linkedin.com/in/kirill-dremov" target="_blank" rel="noopener noreferrer" className="cta-btn secondary">
              View LinkedIn
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CV;